/**
 * InterpolationSection Component
 * 
 * Handles interpolation parameters, execution, and results display.
 * Shows removed points count, max time span input, and interpolation results.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import InputField from '@/shared/components/forms/inputs/InputField';
import SubmitButton from '@/shared/components/buttons/SubmitButton';
import DataChart from '@/shared/components/charts/DataChart';
import DecimalPrecisionSection from '@/shared/components/forms/selectors/DecimalPrecisionSection';
import type { InterpolatedPoint, InterpolationInfo } from '../types/dataCloud.types';
import type { UploadProgress } from '@/shared/components/feedback/ProgressTracker';
import type { DecimalPrecision } from '@/types/common';

export interface InterpolationSectionProps {
  /** Number of removed points from regression */
  removedPoints: number;
  /** Maximum time span for interpolation in minutes */
  maxTimeSpan: string;
  /** Callback when max time span changes */
  onMaxTimeSpanChange: (value: string) => void;
  /** Decimal precision applied to regression output */
  decimalPrecision: DecimalPrecision;
  /** Callback when decimal precision changes */
  onDecimalPrecisionChange: (value: DecimalPrecision) => void;
  /** Callback to trigger interpolation */
  onInterpolate: () => void;
  /** Whether interpolation is in progress */
  isInterpolating: boolean;
  /** Current progress for interpolation */
  progress: UploadProgress | null;
  /** Interpolation result information */
  interpolationInfo: InterpolationInfo | null;
  /** Interpolated data points */
  interpolatedData: InterpolatedPoint[];
  /** Y-axis label for chart */
  yAxisLabel: string;
  /** Whether file2 (power file) is loaded */
  hasFile2: boolean;
}

export const InterpolationSection: React.FC<InterpolationSectionProps> = ({
  removedPoints,
  maxTimeSpan,
  onMaxTimeSpanChange,
  decimalPrecision,
  onDecimalPrecisionChange,
  onInterpolate,
  isInterpolating,
  progress,
  interpolationInfo,
  interpolatedData,
  yAxisLabel,
  hasFile2,
}) => {
  const { t } = useTranslation('dataCloud');

  return (
    <div role="region" aria-label={t('interpolation.perform')}>
      {/* Interpolation Parameters */}
      <div className="mt-8 space-y-6">
        {/* Removed points — informational text only (not an editable field) */}
        <p className="text-foreground text-xs sm:text-sm md:text-base">
          <span className="font-medium">{t('removedPoints')}:</span>{' '}
          <span>{removedPoints !== undefined ? removedPoints : 0}</span>
        </p>

        {/* Max time span input */}
        <InputField
          label={t('interpolation.maxTimeSpan') + ':'}
          value={maxTimeSpan}
          onChange={onMaxTimeSpanChange}
          aria-label={t('interpolation.maxTimeSpan')}
        />

        {/* Decimal precision (applies to regression output) */}
        <DecimalPrecisionSection
          value={decimalPrecision}
          onChange={onDecimalPrecisionChange}
        />

        {/* Interpolation button */}
        <div className="flex justify-end">
          <SubmitButton
            onClick={onInterpolate}
            disabled={isInterpolating || !maxTimeSpan || !hasFile2}
            aria-busy={isInterpolating}
          >
            {isInterpolating
              ? t('interpolation.interpolating', {
                  progress: progress?.processingProgress || 0,
                })
              : t('interpolation.perform')}
          </SubmitButton>
        </div>
      </div>

      {/* Interpolation Results Info */}
      {interpolationInfo && (
        <div 
          className="mt-6 p-4 bg-muted rounded-lg"
          role="status"
          aria-label={t('interpolation.completed')}
        >
          <p className="font-medium text-foreground mb-2">
            {t('interpolation.completed')}:
          </p>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li>
              {t('interpolation.addedPoints')}:{' '}
              <span className="font-medium">{interpolationInfo.addedPoints}</span>
            </li>
            <li>
              {t('interpolation.totalPoints')}:{' '}
              <span className="font-medium">{interpolationInfo.totalPoints}</span>
            </li>
            <li>
              {t('interpolation.removedPoints')}:{' '}
              <span className="font-medium">{interpolationInfo.removedPoints}</span>
            </li>
          </ul>
        </div>
      )}

      {/* Interpolated Data Chart */}
      {interpolatedData.length > 0 && (
        <div className="mt-6" role="region" aria-labelledby="data-cloud-interpolation-heading">
          <h3 id="data-cloud-interpolation-heading" className="text-lg font-semibold mb-4 text-foreground">
            {t('interpolation.interpolatedPowerData')}
          </h3>
          <div className="bg-card rounded-lg shadow-sm border border-border">
            <DataChart
              data={interpolatedData.map((point) => ({
                ...point,
                [yAxisLabel]: point.value,
              }))}
              valueKey={yAxisLabel}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default InterpolationSection;
